import {useAuth} from '@apps-common/auth-react';
import RejectIcon from '@mui/icons-material/Close';
import KeepIcon from '@mui/icons-material/Done';
import {Box, Button, Slide, TextField, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {Map, TileLayer, VectorLayer} from '@planet/layers';
import {transformExtent} from 'ol/proj';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {MAPBOX_DARK} from '../config.js';
import {getImageryURL} from '../util/collection.js';
import {getExtentForGeometry} from '../util/map.js';
import {DetectInfo} from './DetectInfo.jsx';
import {ProgressCircle} from './ProgressCircle.jsx';
import {SizerCallback} from './SizerCallback.js';
import {YayOrNay} from './YayOrNay.jsx';
import {Divider, Drawer, RightFrame} from './modal.js';
import {
  ExportLink,
  KeepButton,
  MapParent,
  RejectButton,
  Toolbar,
  featureStyle,
} from './review-common.jsx';

export const ReviewStep = ({detections, layer, feed, onExport, onClose}) => {
  const [position, setPosition] = useState(0);
  const [detects, setDetects] = useState([...detections]);
  const [doNext, setDoNext] = useState(true);
  const [slideDir, setSlideDir] = useState('left');
  const auth = useAuth();

  const detectStyle = useMemo(() => featureStyle(feed), [feed]);

  useEffect(() => {
    setPosition(0);
    setDetects([...detections]);
  }, [detections]);

  const findIndexForId = useCallback(
    id => {
      if (!id) {
        return -1;
      }
      for (let i = 0; i < detections.length; i++) {
        const f = detections[i];
        if (!f) {
          continue;
        }
        if (f.id && String(f.id) === String(id)) {
          return i;
        }
        if (f.properties) {
          if (f.properties.id && String(f.properties.id) === String(id)) {
            return i;
          }
          if (
            (f.properties.source_item_id &&
              String(f.properties.source_item_id) === String(id)) ||
            (f.properties.source_item &&
              String(f.properties.source_item) === String(id))
          ) {
            return i;
          }
        }
      }
      return -1;
    },
    [detections],
  );

  const handleJumpToId = useCallback(
    val => {
      const idx = findIndexForId(val);
      if (idx >= 0) {
        setPosition(idx + 1);
      }
    },
    [findIndexForId],
  );

  const flagDetect = useCallback(
    flag => {
      const nextDetects = [...detects];
      nextDetects[position - 1].properties = {
        ...nextDetects[position - 1].properties,
        _include: flag,
      };
      setDetects(nextDetects);

      if (position >= detects.length) {
        onExport(nextDetects);
      } else {
        setSlideDir(flag ? 'left' : 'right');
        setDoNext(false);
      }
    },
    [detects, position, onExport, setDoNext],
  );

  const exportSelected = useCallback(() => {
    onExport(detects.slice(0, position));
  }, [detects, position, onExport]);

  const exportAll = useCallback(() => {
    const all = detections.map(feature => ({
      ...feature,
      properties: {
        ...feature.properties,
        _include: true,
      },
    }));
    onExport(all);
  }, [detections, onExport]);

  const mapRef = useRef();

  useEffect(() => {
    if (mapRef.current) {
      const feature = detects[position - 1];
      if (feature) {
        const extent = transformExtent(
          getExtentForGeometry(feature.geometry),
          'EPSG:4326',
          'EPSG:3857',
        );
        const view = mapRef.current.getView();
        view.fit(extent, {
          padding: [30, 30, 30, 30],
          maxZoom: 18,
        });
      }
    }
  }, [position, detects]);

  useEffect(() => {
    // the opening of the modal was causing a size
    //  quirk. This made sure the map fit properly.
    if (mapRef.current) {
      mapRef.current.updateSize();
    }
  }, []);

  // Add keyboard bindings
  useEffect(() => {
    const keyFn = evt => {
      const k = evt.key;
      if (k === 'f' || k === '5') {
        flagDetect(true);
      } else if (k === 'r' || k === '8') {
        flagDetect(false);
      }
    };

    window.addEventListener('keypress', keyFn);
    return () => {
      window.removeEventListener('keypress', keyFn);
    };
  }, [flagDetect]);

  const imageryURL = layer ? getImageryURL(layer, auth.user?.accessToken) : '';

  return (
    <>
      <Drawer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            padding: 4,
          }}
        >
          <Box
            sx={{
              flex: 1,
            }}
          >
            <Typography variant="h5">Review detections for download</Typography>

            <Box
              sx={{
                py: 3,
              }}
            >
              <Typography variant="body1">
                Do you want to keep this detect for export?
              </Typography>
            </Box>

            <Grid container>
              <Grid size={6}>
                <Box
                  sx={{
                    padding: 0.5,
                  }}
                >
                  <RejectButton
                    startIcon={<RejectIcon />}
                    variant="outlined"
                    onClick={() => flagDetect(false)}
                    size="large"
                  >
                    Reject
                  </RejectButton>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box
                  sx={{
                    padding: 0.5,
                  }}
                >
                  <KeepButton
                    startIcon={<KeepIcon />}
                    variant="outlined"
                    onClick={() => flagDetect(true)}
                    size="large"
                  >
                    Keep
                  </KeepButton>
                </Box>
              </Grid>
              <Grid align="center" size={6}>
                (R or 8)
              </Grid>
              <Grid align="center" size={6}>
                (F or 5)
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box
          sx={{
            paddingBottom: 2,
            marginTop: 'auto',
          }}
        >
          <Box
            sx={{
              paddingX: 4,
              paddingBottom: 4,
            }}
          >
            <DetectInfo detect={detects[position - 1]} />
          </Box>

          <Divider />

          <Box
            sx={{
              paddingX: 4,
              paddingTop: 2,
            }}
          >
            <ProgressCircle
              value={position}
              maxValue={detections ? detections.length : 0}
              size={22 * 8}
            />
            <Typography
              variant="overline"
              component="div"
              sx={{marginTop: '8px', textAlign: 'center'}}
            >
              Detects reviewed
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <TextField
                variant="standard"
                size="small"
                placeholder="Detect ID"
                sx={{width: '220px'}}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleJumpToId(e.target.value);
                    e.target.blur();
                  }
                }}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{ml: 1}}
                onClick={e => {
                  const input = e.target.parentNode.querySelector(
                    'input[placeholder="Detect ID"]',
                  );
                  if (input) {
                    handleJumpToId(input.value);
                  }
                }}
              >
                Jump
              </Button>
            </Box>
            <Typography
              variant="overline"
              component="div"
              sx={{marginTop: '8px', textAlign: 'center'}}
            >
              Detects reviewed
            </Typography>
          </Box>
          <Box
            sx={{
              marginTop: 2,
              marginBottom: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" gutterBottom>
              Want to skip this step?
            </Typography>
            <Typography variant="body2">
              <ExportLink onClick={exportAll}>Export all</ExportLink>
              {' or '}
              <ExportLink onClick={exportSelected}>
                Export reviewed detections
              </ExportLink>
            </Typography>
          </Box>
        </Box>
      </Drawer>
      <RightFrame>
        <Toolbar
          backDisabled={position <= 1}
          onBack={() => setPosition(Math.max(1, position - 1))}
          onClose={() => onClose()}
        />
        <MapParent>
          <AutoSizer>
            {({height, width}) => (
              <div
                style={{
                  width,
                  height,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              >
                <SizerCallback
                  width={width}
                  height={height}
                  onResize={() => {
                    if (mapRef.current) {
                      mapRef.current.updateSize();
                    }
                  }}
                />
                <Slide
                  direction={slideDir}
                  in={doNext}
                  onEnter={() => setPosition(position + 1)}
                  onExited={() => setDoNext(true)}
                >
                  <div>
                    <Map
                      animation={{duration: 300}}
                      innerRef={node => (mapRef.current = node)}
                      style={{
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        position: 'absolute',
                        borderRadius: 8,
                      }}
                    >
                      <TileLayer url={MAPBOX_DARK} />
                      <TileLayer url={imageryURL} />
                      <VectorLayer
                        data={detects[position - 1]}
                        layerStyle={detectStyle}
                      />
                    </Map>
                  </div>
                </Slide>
              </div>
            )}
          </AutoSizer>
          <Box
            sx={{
              zIndex: 10,
              position: 'absolute',
              top: 20,
              right: 20,
            }}
          >
            {!!detects[position - 1] &&
              detects[position - 1].properties._include !== undefined && (
                <YayOrNay include={detects[position - 1].properties._include} />
              )}
          </Box>
        </MapParent>
      </RightFrame>
    </>
  );
};

ReviewStep.propTypes = {
  detections: PropTypes.array,
  layer: PropTypes.string,
  feed: PropTypes.object,
  onExport: PropTypes.func,
  onClose: PropTypes.func,
};
